import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Upload, Camera, Image } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

// Upload Zone Component
export const UploadZone = ({ onImageSelect, isLoading }) => {
  const { t } = useLanguage();
  const [isDragging, setIsDragging] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile device on mount and keep it updated on resize.
  // setIsMobile is a stable setter, so [] is the correct (complete) dependency list.
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragIn = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragOut = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        onImageSelect(file);
      } else {
        toast.error(t.invalidImage);
      }
    }
  }, [onImageSelect, t]);

  const handleFileInput = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        onImageSelect(file);
      } else {
        toast.error(t.invalidImage);
      }
    }
  };

  const openCamera = () => {
    if (!isLoading) {
      document.getElementById('camera-input').click();
    }
  };

  const openGallery = () => {
    if (!isLoading) {
      document.getElementById('gallery-input').click();
    }
  };

  return (
    <div
      data-testid="upload-zone"
      className={`upload-zone p-8 md:p-16 flex flex-col items-center justify-center text-center ${isDragging ? 'dragging' : ''}`}
      onDragEnter={handleDragIn}
      onDragLeave={handleDragOut}
      onDragOver={handleDrag}
      onDrop={handleDrop}
    >
      {/* Hidden input for camera (mobile) */}
      <input
        id="camera-input"
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFileInput}
        disabled={isLoading}
        data-testid="camera-input"
      />

      {/* Hidden input for gallery */}
      <input
        id="gallery-input"
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={handleFileInput}
        disabled={isLoading}
        data-testid="gallery-input"
      />

      <div className="mb-6">
        <div className="w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center mx-auto" style={{ backgroundColor: 'rgba(163, 177, 138, 0.2)' }}>
          <Upload className="w-8 h-8 md:w-10 md:h-10" style={{ color: '#1A3C34' }} />
        </div>
      </div>

      {/* Mobile: Show camera and gallery buttons */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4 w-full max-w-xs">
        <button
          onClick={openCamera}
          disabled={isLoading}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-full font-body font-medium transition-all duration-300 hover:scale-105 active:scale-95"
          style={{
            backgroundColor: '#1A3C34',
            color: '#F9F7F2'
          }}
          data-testid="camera-button"
        >
          <Camera className="w-5 h-5" />
          {t.takePhoto}
        </button>

        <button
          onClick={openGallery}
          disabled={isLoading}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-full font-body font-medium transition-all duration-300 hover:scale-105 active:scale-95 border-2"
          style={{
            borderColor: '#1A3C34',
            color: '#1A3C34',
            backgroundColor: 'transparent'
          }}
          data-testid="gallery-button"
        >
          <Image className="w-5 h-5" />
          {t.chooseFromGallery}
        </button>
      </div>

      {/* Desktop: drag and drop hint */}
      <p className="font-body text-sm hidden md:block" style={{ color: '#6B705C' }}>
        {t.orDragHere}
      </p>
      <p className="font-body text-xs mt-2" style={{ color: '#6B705C' }}>
        {t.acceptedFormats}
      </p>
    </div>
  );
};
